import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  labelJa,
  labelJaForCategory,
  formatFormValueDisplayForCategory,
} from "../lib/applicationFormFieldLabels";
import { filterFormEntries } from "../lib/applicationFormDisplay";
import "./ApplicationDetail.css";
import { AuthImage } from "../lib/authFile";

// ステータス確定前の最終確認メッセージ。確定すると申請中には戻せない
const CONFIRM_APPROVE =
  "この申請を「承認」にします。よろしいですか？\n" +
  "※ 承認すると申請中には戻せません。";
const CONFIRM_REJECT =
  "この申請を「未承認」にします。よろしいですか？\n" +
  "※ 未承認にすると申請中には戻せません。";

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [appData, setAppData] = useState(null);
  const [error, setError] = useState(null);
  // マイナンバーの全桁（表示中のみ保持。30 秒で自動的にマスクに戻す。docs/spec_mynumber.md 7 章・U-8）
  const [revealed, setRevealed] = useState(null); // { self, family: { [id]: number } }
  const [revealing, setRevealing] = useState(false);

  useEffect(() => {
    if (!revealed) return undefined;
    const timer = setTimeout(() => setRevealed(null), 30000);
    return () => clearTimeout(timer);
  }, [revealed]);

  const handleRevealMyNumber = async () => {
    if (revealed) {
      setRevealed(null);
      return;
    }
    setRevealing(true);
    try {
      // 全桁は専用 API でのみ取得する（閲覧は監査ログに記録される）
      const res = await api.post(`/admin/applications/${id}/mynumber/reveal/`);
      const family = {};
      (res.data?.family || []).forEach((f) => { family[f.id] = f.myNumber; });
      setRevealed({ self: res.data?.self || null, family });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert(detail || "マイナンバーを表示できませんでした。");
    } finally {
      setRevealing(false);
    }
  };

  const revealButton = (
    <button type="button" className="btn-secondary-print no-print" onClick={handleRevealMyNumber} disabled={revealing}
      style={{ marginLeft: 8, padding: "2px 10px", fontSize: "0.85rem" }}>
      {revealed ? "隠す" : "全桁を表示（30秒）"}
    </button>
  );

  useEffect(() => {
    api
      .get(`/admin/applications/${id}/`)
      .then((res) => setAppData(res.data))
      .catch((err) => {
        console.error(err);
        setError("申請内容を取得できませんでした。");
      });
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
        <button
          type="button"
          className="btn-back no-print adm-btn"
          onClick={() => navigate("/admin/applications")}
        >
          戻る
        </button>
      </div>
    );
  }

  if (!appData) return <div className="mt-4">Loading...</div>;

  const form = appData.formData || {};
  const category = appData.category || "";
  // 承認・却下できるのは「申請中」の申請のみ
  const isDecidable = appData.status === "SUBMITTED";
  const formEntries = filterFormEntries(category, form);

  // 画像は認証付きの配信 API から取得する（/media/ の直リンクは使わない。docs/spec_file_access.md）
  const mynumberImg =
    appData.mynumber_image_api_url ||
    appData.attachment_api_url;

  const handleApprove = async () => {
    // 確定済みの申請は変更させない（申請一覧のセレクトと同じルール）
    if (!isDecidable) return;
    if (!window.confirm(CONFIRM_APPROVE)) return;

    try {
      await api.post(`/admin/applications/${id}/approve/`);
      alert("承認しました");
      navigate("/admin/applications", { state: { refreshList: true } });
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      alert(detail ? `承認処理でエラーが発生しました。\n${detail}` : "承認処理でエラーが発生しました。");
    }
  };

  const handleReject = async () => {
    if (!isDecidable) return;
    if (!window.confirm(CONFIRM_REJECT)) return;

    try {
      await api.post(`/admin/applications/${id}/reject/`);
      alert("却下しました");
      navigate("/admin/applications", { state: { refreshList: true } });
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      alert(detail ? `却下処理でエラーが発生しました。\n${detail}` : "却下処理でエラーが発生しました。");
    }
  };

  return (
    <div className="container mt-4 application-detail-page">
      <div id="application-print-root">
        <h2 className="mb-3 application-detail-title">{appData.title}</h2>

        <div className="card p-4 mb-3">
          <p>
            <strong>社員コード:</strong> {appData.applicant_memberId}
          </p>
          <p>
            <strong>申請者:</strong> {appData.applicant_name}
          </p>
          
          {/*}
          <p>
            <strong>申請名:</strong> {appData.title}
          </p>
          */}
          <p>
            <strong>申請日:</strong>{" "}
            {(appData.submitted_at || appData.created_at)
              ? new Date(appData.submitted_at || appData.created_at).toLocaleDateString("ja-JP", { year: "numeric", month: "2-digit", day: "2-digit" })
              : "—"}
          </p>
        </div>

        <div className="card p-4 mb-4">
          <h4 className="application-detail-section-title">申請内容</h4>

          {(category === "MYNUMBER" || category === "joined") && appData.myNumber_masked && (
            <div className="application-detail-field">
              <span className="application-detail-label">マイナンバー：</span>
              <span className="application-detail-value">{revealed?.self || appData.myNumber_masked}</span>
              {revealButton}
            </div>
          )}

          {(category === "MYNUMBER" || category === "joined") && mynumberImg && (
            <div className="application-detail-mynumber-image">
              <strong className="application-detail-image-caption">マイナンバー確認書類（画像）</strong>
              <AuthImage url={mynumberImg} alt="マイナンバー確認書類" className="application-detail-img"
                fallback={<p className="text-muted">画像を読み込めません。</p>} />
            </div>
          )}

          {category === "FAMILY" && Array.isArray(appData.family_mynumbers) && appData.family_mynumbers.length > 0 && (
            <div className="application-detail-family-mynumbers">
              <strong className="application-detail-image-caption">家族のマイナンバー</strong>
              {appData.family_mynumbers.map((fm, i) => (
                <div key={i} className="application-detail-family-mynumber-row" style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
                  <div className="application-detail-field">
                    <span className="application-detail-label">続柄・氏名：</span>
                    <span className="application-detail-value">{[fm.relation, fm.nameKanji].filter(Boolean).join(" ") || "—"}</span>
                  </div>
                  <div className="application-detail-field">
                    <span className="application-detail-label">マイナンバー：</span>
                    <span className="application-detail-value">{revealed?.family?.[fm.id] || fm.myNumber_masked || "—"}</span>
                    {fm.myNumber_masked && i === 0 && revealButton}
                  </div>
                  {fm.image_api_url && (
                    <AuthImage url={fm.image_api_url} alt={`${fm.relation} ${fm.nameKanji} マイナンバー`} className="application-detail-img"
                      fallback={<p className="text-muted">画像を読み込めません。</p>} />
                  )}
                </div>
              ))}
            </div>
          )}

          {formEntries.length === 0 &&
            !((category === "MYNUMBER" || category === "joined") && mynumberImg) &&
            !(category === "FAMILY" && Array.isArray(appData.family_mynumbers) && appData.family_mynumbers.length > 0) && (
            <p className="text-muted">申請内容がありません。</p>
          )}

          <div className="application-detail-body">
            {formEntries.map(([key, val]) => (
              <div key={key} className="application-detail-field">
                <span className="application-detail-label">{labelJaForCategory(category, key)}：</span>
                <span className="application-detail-value">
                  {formatFormValueDisplayForCategory(category, key, val)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="button-group no-print">
        <button type="button" className="btn-secondary-print" onClick={handlePrint}>
          印刷
        </button>
        {/*}
        <button
          type="button"
          className="btn-success"
          onClick={isDecidable ? handleApprove : undefined}
          disabled={!isDecidable}
          title={!isDecidable ? "確定済みの申請はステータスを変更できません" : undefined}
          style={!isDecidable ? { backgroundColor: '#6c757d', borderColor: '#6c757d', cursor: 'not-allowed', opacity: 0.65 } : {}}
        >
          承認する
        </button>
        <button
          type="button"
          className="btn-danger"
          onClick={category === "RETIREMENT" || !isDecidable ? undefined : handleReject}
          disabled={category === "RETIREMENT" || !isDecidable}
          title={!isDecidable ? "確定済みの申請はステータスを変更できません" : undefined}
          style={category === "RETIREMENT" || !isDecidable ? { backgroundColor: '#6c757d', borderColor: '#6c757d', cursor: 'not-allowed', opacity: 0.65 } : {}}
        >
          却下する
        </button>
        */}
        <button
          type="button"
          className="btn-back adm-btn"
          onClick={() => navigate("/admin/applications")}
        >
          戻る
        </button>
      </div>
    </div>
  );
};

export default ApplicationDetail;
